import {
  FreeTextTeaserEntry,
  TeaserEntry,
  AudioTeaserEntry,
} from "../helper/interfaces";
import React, { useContext } from "react";

import { AppTheme, ContentType } from "../helper/enums";
import { getAuthorNames } from "../helper/author";
import { AppContext } from "../provider/AppContextProvider";
import TeaserInfoBox from "./TeaserInfoBox";
import TeaserQualifio from "./TeaserQualifio";
import { getTeaserTypeIcons, getTeaserImageUrl } from "../helper/images";
import TeaserCollectiveBox from "./TeaserCollectiveBox";
import TeaserAd from "./TeaserAd";
import CustomImage from "./ui/Image";
import { AdInformationInterface } from "../helper/PageInformationInterface";
import TeaserVideo from "./TeaserVideo";
import TeaserImage from "./TeaserImage";
import TeaserLinkBox from "./TeaserLinkBox";
import TeaserLiveticker from "./TeaserLiveticker";
import { useLoggedRouter } from "../helper/useLoggedRouter";
import { getLinkObjectFromTeaser } from "../helper/link";
import TeaserAdMarker from "./teaserAdMarker";

export interface TeaserData {
  teaser: TeaserEntry;
  className?: string;
  navigate?: (articleId: string, ressortId: string) => void;
  adInformation?: AdInformationInterface;
  ignoreLead?: boolean;
}

function TeaserMinimizedView(data: TeaserData) {
  const [routerPush] = useLoggedRouter();
  const internalNavigate = async (e, linkObject) => {
    e.stopPropagation();
    e.preventDefault();

    if (linkObject.articleId || linkObject.ressortId) {
      if (data.navigate) {
        data.navigate(linkObject.articleId, linkObject.ressortId);
      } else {
        await routerPush(`/article/${encodeURIComponent(data.teaser.id)}`);
      }
    }
  };

  const teaser = data.teaser;

  const image = teaser?.images ? teaser.images[0] : undefined;
  const imageUrl = image && getTeaserImageUrl(image);

  const teaserClassName = `teaser teaser-minimized teaser--type-${
    teaser.type
  } ${imageUrl ? "teaser--image" : ""} ${
    teaser.featured ? "teaser--featured" : ""
  } ${data.className ? data.className : ""}`;

  const { audioPlayer, setAudioPlayer } = useContext(AppContext);

  let icons = getTeaserTypeIcons(teaser.teaserTypes);

  function renderContent() {
    const authorNames = getAuthorNames(teaser.authors);
    return (
      <div className="flex items-stretch">
        {imageUrl && (
          <div className="teaser-image flex-none">
            <div className="aspect-w-16 aspect-h-9 relative">
              <CustomImage
                src={imageUrl}
                //fill
                layout="fill"
                //height="100%"
                //width="150px" height="100%"
                objectFit="cover"
                alt={image.title}
              />
              {teaser.modulePatch && (
                <div className="teaser-module-patch">{teaser.modulePatch}</div>
              )}
              {icons.length > 0 && (
                <div className="teaser__image__icons">
                  {icons.map((icon, i) => (
                    <div
                      key={`icon-${i}`}
                      className={`teaser__image__icon ${icon}`}
                    ></div>
                  ))}
                </div>
              )}
            </div>
            {teaser.type === ContentType.Podcast && (
              <div className="teaser-podcast-play">
                {audioPlayer?.playing &&
                audioPlayer.audioUrl ===
                  (teaser as AudioTeaserEntry).audioUrl ? (
                  <div className="icon-button_pause"></div>
                ) : (
                  <div className="icon-button_play"></div>
                )}
              </div>
            )}
          </div>
        )}
        <div className="teaser-body flex-1">
          {(teaser.paid || teaser.topline) && (
            <div className="teaser-info">
              {teaser.paid && !teaser.hidePlusIcon && <div className="icon-nnplus" aria-label="NN+"></div>}
              <TeaserAdMarker adIdentifier={teaser.adIdentifier === true} />
              {teaser.topline && (
                <div className="teaser-topline">
                  <div>{teaser.topline}</div>
                </div>
              )}
            </div>
          )}
          {teaser.title && <h3 className="teaser-title">{teaser.title}</h3>}
        </div>
      </div>
    );
  }

  function renderContentNb() {
    const authorNames = getAuthorNames(teaser.authors);
    return (
      <div className="flex items-stretch">
        {imageUrl && (
          <div className="teaser-image flex-none">
            <div className="aspect-w-16 aspect-h-9 relative">
              <CustomImage
                src={imageUrl}
                //fill
                fill
                //height="100%"
                //width="150px" height="100%"
                className="object-cover"
                alt={image.title}
              />
              {teaser.modulePatch && (
                <div className="teaser-module-patch">{teaser.modulePatch}</div>
              )}
              {icons.length > 0 && (
                <div className="teaser__image__icons">
                  {icons.map((icon, i) => (
                    <div
                      key={`icon-${i}`}
                      className={`teaser__image__icon ${icon}`}
                    ></div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <div className="teaser-body flex-1">
          {teaser.type === ContentType.Podcast && (
            <div className="teaser-podcast-play">
              {audioPlayer?.playing &&
              audioPlayer.audioUrl === (teaser as AudioTeaserEntry).audioUrl ? (
                <div className="icon-button_pause"></div>
              ) : (
                <div className="icon-button_play"></div>
              )}
            </div>
          )}

          {(teaser.paid || teaser.topline) && (
            <div className="teaser-info">
              {teaser.paid && !teaser.hidePlusIcon && <div className="icon-nnplus" aria-label="NN+"></div>}
              {teaser.topline && (
                <div className="teaser-topline">
                  <div>{teaser.topline}</div>
                </div>
              )}
            </div>
          )}
          {teaser.title && <h3 className="teaser-title">{teaser.title}</h3>}
        </div>
      </div>
    );
  }

  switch (teaser.type) {
    case ContentType.CollectiveBox:
      return <TeaserCollectiveBox teaser={data.teaser} />;

    case ContentType.Podcast:
      return (
        <>
          <button
            onClick={() => {
              setAudioPlayer({
                topline: teaser.topline,
                title: teaser.title,
                playing: !(
                  audioPlayer?.playing &&
                  audioPlayer.audioUrl === (teaser as AudioTeaserEntry).audioUrl
                ),
                audioUrl: (teaser as AudioTeaserEntry).audioUrl,
              });
            }}
            data-name={`article-${teaser.id}`}
            className={teaserClassName}
          >
            {process.env.NEXT_PUBLIC_APP_THEME === AppTheme.nb
              ? renderContentNb()
              : renderContent()}
          </button>
          {/* {(teaser as FreeTextTeaserEntry).linkButtonUrl && (
            <div className="teaser-more">
              <a
                className="teaser-more__link"
                href={(teaser as FreeTextTeaserEntry).linkButtonUrl}
                target="_blank"
                rel="noreferrer"
              >
                {(teaser as FreeTextTeaserEntry).linkButtonText}
              </a>
            </div>
          )} */}
        </>
      );

    case ContentType.InfoBox:
      return <TeaserInfoBox teaser={data.teaser} />;
    case ContentType.InfoBoxTeaser:
      return <TeaserInfoBox teaser={data.teaser} />;
    case ContentType.Linkbox:
      return <TeaserLinkBox teaser={data.teaser} navigate={data.navigate} />;
    case ContentType.Advert:
      return (
        <TeaserAd teaser={data.teaser} adInformation={data.adInformation} />
      );
    case ContentType.Video:
    case ContentType.GlomexVideo:
      return <TeaserVideo teaser={data.teaser} />;
    case ContentType.Image:
      return <TeaserImage teaser={data.teaser} />;
    case ContentType.QualifioBox:
      return <TeaserQualifio teaser={data.teaser} />;
    case ContentType.Liveticker:
      return <TeaserLiveticker teaser={data.teaser} />;

    default:
      break;
  }

  const linkObject = getLinkObjectFromTeaser(teaser);
  const webViewUrl = linkObject.externalLink;

  return (
    <>
      {!webViewUrl && (
        <a
          href={linkObject.internalLink}
          key={`teaser-link-${teaser.id}`}
          onClick={(e) => {
            internalNavigate(e, linkObject);
          }}
          className={teaserClassName}
        >
          {process.env.NEXT_PUBLIC_APP_THEME === AppTheme.nb
            ? renderContentNb()
            : renderContent()}
        </a>
      )}
      {webViewUrl && (
        <a
          href={webViewUrl}
          key={`teaser-link-${teaser.id}`}
          target="_blank"
          rel="noreferrer"
          data-name={`article-${teaser.id}`}
          className={teaserClassName}
        >
          {process.env.NEXT_PUBLIC_APP_THEME === AppTheme.nb
            ? renderContentNb()
            : renderContent()}
        </a>
      )}
      {(teaser as FreeTextTeaserEntry).linkButtonUrl &&
        (teaser as FreeTextTeaserEntry).linkButtonText && (
          <div className="teaser-button" data-name={`article-${teaser.id}`}>
            <a
              className="teaser-button__btn btn btn-primary-dark"
              href={(teaser as FreeTextTeaserEntry).linkButtonUrl}
              target="_blank"
              rel="noreferrer"
            >
              {(teaser as FreeTextTeaserEntry).linkButtonText}
            </a>
          </div>
        )}
    </>
  );
}

export default TeaserMinimizedView;
